<script lang="ts">
  import type {FieldsSetter, Obj} from '@felte/common/dist/esm/types';
  import {slide} from 'svelte/transition';
  import type {Writable} from 'svelte/store';
  import * as yup from 'yup';

  import {CatalystApp} from 'Catalyst/components/CatalystApp';
  import Arrow from 'Components/Common/icons/NextArrow.svelte';
  import Icon from 'Catalyst/components/Icon/Icon.svelte';
  import {
    dealId,
    hasSubmittedPromotion,
    jwtToken,
    patchUpdateInvestor,
    personalInfo,
  } from 'Store/InvestorFlowStore';
  import {SectionNotice} from 'Catalyst/components/SectionNotice';
  import Form from 'Components/Common/FormManager/Form.svelte';
  import Input from 'Components/Common/FormManager/Input.svelte';
  import {getIncentiveTierByCode} from 'Utility/deals/incentive_tier/getIncentiveTiers';

  import {whiteLabeling} from 'Store/WhiteLabelStore';
  import type {CssColor} from 'Catalyst/utility/theme';

  let referrerEmail = '';
  let freeShares = 0;
  let isCodeValid = false;
  let invalidMessage = null;
  let formValid = false;

  let promoCodeData: Writable<{code: string; referrerEmail: string}> = null;

  let validateForm = async () => {};
  let setFormFields: FieldsSetter<Obj, string>;

  const handleSubmit = async () => {
    hasSubmittedPromotion.set(true);
    await patchUpdateInvestor();
  };

  const applyPromoCode = async () => {
    await validateForm();
    if (!formValid) return;

    try {
      const data = await getIncentiveTierByCode(
        $dealId,
        $promoCodeData.code,
        $promoCodeData.referrerEmail,
        $jwtToken
      );

      if (data.success) {
        freeShares = data.freeShares;
        isCodeValid = true;
        $personalInfo.referrer_email = $promoCodeData.referrerEmail;
        $personalInfo.incentive_code = $promoCodeData.code;
        invalidMessage = null;
      } else {
        clearData();
        invalidMessage = data.message;
      }
    } catch (error) {
      console.error('Error applying promo code:', error);
      clearData();
    }
  };

  const clearData = () => {
    isCodeValid = false;
    freeShares = 0;
    $personalInfo.referrer_email = '';
    $personalInfo.incentive_code = '';
  };

  const clearPromoCode = () => {
    clearData();
    setFormFields('code', '');
  };

  const validationSchema = yup.object().shape({
    code: yup.string().label('Promo code').required(),
    referrerEmail: yup.string().label('Referrer email').email().required(),
  });

  $: if ($promoCodeData?.referrerEmail || $promoCodeData?.code) {
    isCodeValid = false;
  }

  $: brandColor = $whiteLabeling.primary_color as CssColor;
</script>

<CatalystApp
  brandColors={[
    {
      key: 'brand-color',
      value: brandColor,
    },
  ]}
>
  <div class="py-[0.75rem]" data-testid="incentive-tier-codes-form">
    <h2
      class="text-[1.125rem] font-inter font-normal font-medium leading-[1.25rem] text-gray-700 align-self-start"
    >
      Enter promo code for incentive
    </h2>
    <Form
      schema={validationSchema}
      on:submit={handleSubmit}
      class="my-[1.5rem]"
      bind:valid={formValid}
      bind:validateForm
      bind:setFormFields
      bind:dataStore={promoCodeData}
    >
      <div class="relative w-full">
        <div class="relative">
          {#if isCodeValid}
            <div
              transition:slide|local={{duration: 250, axis: 'x'}}
              class="absolute left-3 top-0 py-[0.750rem] flex items-center"
            >
              <Icon
                name="check_circle"
                filled
                class="text-[--palettes-brand-color-600]"
              />
            </div>
          {/if}
          <Input
            name="code"
            placeholder="Enter promo code"
            class="{isCodeValid
              ? '!pl-10 !border-[--palettes-brand-color-600] text-[--palettes-brand-color-600]'
              : '!pl-3.5'} focus:outline-none border border-gray-300 placeholder:text-gray-500 placeholder:font-Inter placeholder:text-[1rem] placeholder:font-normal placeholder:font-[400] placeholder:leading-[1.5rem] transition-all duration-200"
          />
        </div>
        <div class="absolute right-0 top-0 py-[0.625rem] flex items-center">
          {#if isCodeValid}
            <button
              type="button"
              data-testid="clear-button"
              on:click={clearPromoCode}
              class="px-3 flex items-center text-gray-500 hover:text-gray-700 transition-colors leading-[1.5] py-[0.125rem]"
            >
              <Icon name="close" />
            </button>
          {:else}
            <button
              type="button"
              on:click={applyPromoCode}
              class="px-3 text-center text-[--palettes-brand-color-500] font-inter text-[1rem] font-normal font-[500] leading-[1.5rem] leading-[1.5]"
            >
              Apply
            </button>
          {/if}
        </div>
      </div>
      <Input
        bind:value={referrerEmail}
        name="referrerEmail"
        placeholder="Referrer's email address"
        class="pl-[0.88rem] py-[1.28rem] focus:outline-none border border-gray-300 placeholder:text-gray-500 placeholder:font-Inter placeholder:text-[1rem] placeholder:font-normal placeholder:font-[400] placeholder:leading-[1.5rem]"
      />
      <div
        class="rounded-[0.25rem] border border-gray-300 bg-[#F9FAFB] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] flex py-[0.625rem] px-[0.88rem] items-center gap-[0.5rem] self-stretch text-[#667085] font-Inter text-[1rem] font-normal font-[400] leading-[1.5rem]"
      >
        <p
          class="text-[var(--Gray-500,#667085)] font-Inter text-[1rem] font-normal leading-[1.5rem]"
        >
          {freeShares} shares
        </p>
      </div>
      {#if !isCodeValid && invalidMessage}
        <SectionNotice
          semantic="error"
          message={invalidMessage}
          class="!mt-3"
        />
      {/if}
      <button
        id="accept_invitation"
        type="submit"
        data-testId="submit"
        class="flex w-full mt-[1.5rem] p-[0.625rem] px-[1.125rem] justify-center items-center gap-[0.5rem] self-stretch rounded-[1.75rem] disabled:opacity-[0.5] bg-[--palettes-brand-color-600] disabled:bg-[--palettes-brand-color-500] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
        disabled={!isCodeValid}
      >
        <div
          class="text-[#FFF] font-inter text-[1rem] font-normal font-[500] leading-[1.5rem]"
        >
          Continue
        </div>
        <div class="arrow"><Arrow /></div>
      </button>
    </Form>
    <SectionNotice
      semantic="info"
      message="You are almost there! Just one more step to go to avail free your
        shares."
    />
  </div>
</CatalystApp>
